'use client';

/**
 * VerticalPricing: shared single-tier pricing card + note + secondary
 * link. Used by all 6 verticals.
 *
 * Two pricing shapes (discriminated by `copy.pricing.kind`):
 *
 *   - 'numeric': a number with the locale-appropriate currency symbol
 *     and period. 29 on retail / restaurants / crm, 129 on services /
 *     events / b2b. The same number in every currency, which is resolved
 *     from `lang` via `getCurrencyForLocale` and mapped through
 *     CURRENCY_DISPLAY (matches the JSON-LD priceCurrency elsewhere on
 *     the site). The period string always carries "billed annually".
 *
 *   - 'custom': no currency, just a label like "Custom" or "Sur mesure".
 *     No vertical uses it today (only the /components catalog does); it is
 *     the shape for Large Business, which never shows a number.
 *
 * The "Reusing .retail-pricing-grid" CSS class name from the original
 * implementation stays — it's a 1-tier-plus-note grid that works for any
 * vertical, just historically named after the first vertical to use it.
 */

import CtaLink from '@/components/common/CtaLink';
import React from 'react';
import SectionLabel from '../../home/SectionLabel';
import { Locale } from '../../../../../i18n-config';
import { getCurrencyForLocale } from '@/utils/technical-seo';
import type { PricingCopy } from './types';

type Props = {
  sectionNum: string;
  lang: Locale;
  copy: PricingCopy;
};

const CURRENCY_DISPLAY: Record<string, { symbol: string; before: boolean }> = {
  EUR: { symbol: '€', before: false },
  CHF: { symbol: 'CHF', before: true },
  USD: { symbol: '$', before: true },
};

const VerticalPricing = ({ sectionNum, lang, copy }: Props) => {
  const currencyCode = getCurrencyForLocale(lang);
  const display = CURRENCY_DISPLAY[currencyCode] ?? CURRENCY_DISPLAY.EUR;

  return (
    <section
      className="px-6 py-24 lg:py-28"
      style={{
        background: '#0A0E27',
        borderTop: '1px solid rgba(255,255,255,0.05)',
        color: '#F5F7FA',
      }}
    >
      <div className="mx-auto max-w-[1200px]">
        <SectionLabel num={sectionNum} label={copy.label} />
        <h2
          className="font-bold tracking-tight"
          style={{
            fontSize: 'clamp(28px, 3.4vw, 48px)',
            lineHeight: 1.1,
            letterSpacing: '-0.02em',
            margin: '12px 0 14px',
            maxWidth: 820,
          }}
        >
          {copy.headlineA} <span style={{ color: '#B4C1D8' }}>{copy.headlineB}</span>
        </h2>

        <div className="mt-12 retail-pricing-grid" style={{ display: 'grid', gap: 32, alignItems: 'start' }}>
          <div className="sum-plan featured">
            <div className="sum-plan-tier">{copy.tier}</div>
            <div className="flex items-baseline gap-1.5 mt-2">
              {copy.pricing.kind === 'numeric' ? (
                <>
                  {display.before && <div style={{ color: '#B4C1D8', fontSize: 18 }}>{display.symbol}</div>}
                  <div className="sum-plan-price">{copy.pricing.price}</div>
                  {!display.before && <div style={{ color: '#B4C1D8', fontSize: 18 }}>{display.symbol}</div>}
                  <div style={{ color: '#B4C1D8', fontSize: 14, marginLeft: 4 }}>{copy.pricing.period}</div>
                </>
              ) : (
                <div className="sum-plan-price">{copy.pricing.priceLabel}</div>
              )}
            </div>
            <div className="sum-plan-sub">{copy.sub}</div>
            <ul className="sum-plan-list">
              {copy.bullets.map((b) => (
                <li key={b}>
                  <span className="sum-plan-check">✓</span>
                  {b}
                </li>
              ))}
            </ul>
            <CtaLink
              href={copy.ctaPrimary.href}
              className="sum-cta-primary"
              style={{ width: '100%', marginTop: 'auto' }}
            >
              {copy.ctaPrimary.label}
            </CtaLink>
          </div>

          <div style={{ paddingTop: 8 }}>
            <p
              style={{
                color: '#B4C1D8',
                fontSize: 16,
                lineHeight: 1.6,
                margin: '0 0 24px',
                maxWidth: 560,
              }}
            >
              {copy.note}
            </p>
            <CtaLink href={copy.ctaSecondary.href} className="sum-cta-ghost">
              {copy.ctaSecondary.label} →
            </CtaLink>
          </div>
        </div>
      </div>
    </section>
  );
};

export default VerticalPricing;
